import { useEffect, useMemo, useState } from "react";

import Overlay from "../../Componentes/Overlay";
import Modal from "../../Componentes/Utils/Modal";
import Formulario from "../../Componentes/Utils/Formulario";
import FormComp from "../../Componentes/FormComp";
import SmallLoading from "../../Componentes/Utils/SmallLoading";

import { useModal } from "../../contexts/ModalContext";
import { useToast } from "../../contexts/ToastContext";
import useApi from "../../hooks/useApi";

// Normaliza qualquer formato de data ("YYYY-MM-DD", "YYYY-MM-DD HH:mm:ss" ou
// objeto Date) para "YYYY-MM-DD", que é o que o <input type="date"> aceita.
const toInputDate = (value) => {
    if (!value) return '';
    if (typeof value === 'string') return value.slice(0, 10);
    if (value instanceof Date && !isNaN(value)) {
        return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(value.getDate()).padStart(2, '0')}`;
    }
    return '';
};

const addDaysISO = (isoDate, days) => {
    if (!isoDate) return '';
    const base = new Date(`${isoDate}T00:00:00`);
    base.setDate(base.getDate() + days);
    const yyyy = base.getFullYear();
    const mm = String(base.getMonth() + 1).padStart(2, '0');
    const dd = String(base.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
};

/**
 * Espelha a regra do backend (`UpdateFolga`):
 *   - Sem dias_gozo válido (ausente/0) ou sem distância → incremento 0
 *     (sugestão = data_folga, igual ao fallback do backend).
 *   - Caso contrário: incremento = gozo + viagem - 1, viagem = 1 (<300) ou 4 (>=300).
 */
const incrementoRetorno = (param) => {
    if (!param) return 0;

    const diasGozo = Number(param.gozo_dias);
    if (!Number.isFinite(diasGozo) || diasGozo <= 0) return 0;

    const distancia = Number(param.distancia);
    if (!Number.isFinite(distancia)) return 0;

    const diasViagem = distancia < 300 ? 1 : 4;
    return diasGozo + diasViagem - 1;
};

const ModalEditFolga = ({ folga, fetchData, protheus }) => {

    const { closeModal } = useModal();
    const { notifySuccess, notifyError } = useToast();
    const api = useApi();

    const [loading, setLoading] = useState(false);

    const [dataFolga, setDataFolga] = useState(toInputDate(folga?.data));
    const [dataPagamento, setDataPagamento] = useState(toInputDate(folga?.data_pagamento));
    const [dataRetorno, setDataRetorno] = useState(toInputDate(folga?.data_retorno));

    /** Parametrização atual do colaborador vinda do modelo novo (centro_custo_folga + faixa). */
    const [parametros, setParametros] = useState(null);
    const [loadingParam, setLoadingParam] = useState(true);

    // Busca os valores ATUAIS das tabelas novas (centro_custo_folga + obra_municipio_km
    // + centro_custo_faixa_folga) via GetDistance, para basear a sugestão de retorno
    // na configuração atual — não no que ficou gravado na folga antiga.
    useEffect(() => {
        let cancelled = false;
        const carregar = async () => {
            const centro_custo = protheus?.CENTRO_CUSTO ?? null;
            const municipio    = protheus?.MUNICIPIO    ?? null;
            const estado       = protheus?.ESTADO       ?? null;

            if (!centro_custo || !municipio || !estado) {
                if (!cancelled) {
                    setParametros(null);
                    setLoadingParam(false);
                }
                return;
            }

            try {
                const { data } = await api.post('/Painel/API/Colaborador/GetDistance/', {
                    centro_custo,
                    municipio,
                    estado,
                });
                if (!cancelled) setParametros(data || null);
            } catch (e) {
                console.warn('[ModalEditFolga] sem parametrização nova; sugerindo data_retorno = data_folga.', e?.response?.data);
                if (!cancelled) setParametros(null);
            } finally {
                if (!cancelled) setLoadingParam(false);
            }
        };
        carregar();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [protheus?.CENTRO_CUSTO, protheus?.MUNICIPIO, protheus?.ESTADO]);

    useEffect(() => {
        setDataFolga(toInputDate(folga?.data));
        setDataPagamento(toInputDate(folga?.data_pagamento));
        setDataRetorno(toInputDate(folga?.data_retorno));
    }, [folga]);

    const incremento = useMemo(() => incrementoRetorno(parametros), [parametros]);

    const handleChangeDataFolga = (value) => {
        setDataFolga(value);

        if (value) {
            const base = new Date(`${value}T00:00:00`);
            base.setDate(base.getDate() - 60);
            const sugerido = `${base.getFullYear()}-${String(base.getMonth() + 1).padStart(2, '0')}-${String(base.getDate()).padStart(2, '0')}`;
            setDataPagamento(sugerido);
        }

        // Sugestão alinhada ao backend usando os valores das tabelas novas;
        // sem parametrização => retorno = data_folga.
        setDataRetorno(value ? addDaysISO(value, incremento) : '');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {

            await api.post('/Painel/API/Colaborador/UpdateFolga/', {
                id: folga.id,
                matricula: folga.matricula,
                data_folga: dataFolga,
                data_pagamento: dataPagamento,
                data_retorno: dataRetorno
            });

            await fetchData();
            notifySuccess("Folga atualizada com sucesso.");
            closeModal();

        } catch (e) {
            console.error(e);
            const msg = e?.response?.data?.message || "Erro ao atualizar folga.";
            notifyError(msg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Overlay state={true}>
                <Modal size="md" header={true} close={closeModal}>
                    <Formulario onSubmit={handleSubmit}>
                        <FormComp
                            title="DATA INÍCIO FOLGA"
                            type="date"
                            valor={dataFolga}
                            changeFunc={(e) => handleChangeDataFolga(e.target.value)}
                            required
                        />
                        <FormComp
                            title={
                                loadingParam
                                    ? "DATA DE RETORNO (carregando parametrização…)"
                                    : parametros
                                    ? "DATA DE RETORNO"
                                    : "DATA DE RETORNO (sem parametrização — sugestão = início)"
                            }
                            type="date"
                            valor={dataRetorno}
                            changeFunc={(e) => setDataRetorno(e.target.value)}
                        />
                        <FormComp
                            title="DATA DO PAGAMENTO"
                            type="date"
                            valor={dataPagamento}
                            changeFunc={(e) => setDataPagamento(e.target.value)}
                        />
                        <FormComp
                            type="submit"
                            valor="SALVAR"
                        />
                    </Formulario>
                </Modal>
            </Overlay>
            {loading && (
                <Overlay state={true}>
                    <Modal size="sm">
                        <SmallLoading />
                    </Modal>
                </Overlay>
            )}
        </>
    );
};

export default ModalEditFolga;
